import Game from '@Game';
import Network from '@Networks/Localhost';
import BaseScene from '@Scenes/BaseScene';
import EventEmitter from '@Utils/Events/EventEmitter';
import EventChannels from '@Config/event_channels';
import AnalyticsEvents from '@Config/analytics_events';

/*---------------- Config ------------------------*/
import * as Scenes from '@Config/scenes';
import KeyCodes from '@Config/keycodes';

/*---------------- Utils ------------------------*/
import ObjectExport from '@Utils/Object/Export';
import Delay from '@Utils/Time/Delay';
import Extend from '@Utils/Object/Extend';


/**
 * Main scene
 * @author charlie.bodman@kanoapps.com
 */
class MainScene extends BaseScene
{
    private playing: boolean;

    private checkpointUsed: boolean;

    /**
     * @todo type this
     */
    private checkpoints: {
        player: FreeRider.PlayerExported,
        score: any
    }[];

    /**
     * Contructor
     */
    constructor()
    {
        super(Scenes.MAIN);

        this.checkpoints = [];
    }

    /**
     * Create part of the phaser scene lifecycle
     */
    public async create(options: { useCheckpoint?: boolean })
    {
        this.playing = false;

        /**
         * Launch parallel scenes
         */
        if (Network.getSupportedAPIs().indexOf('context.getPlayersAsync') !== -1)
        {
            this.scene.launch(Scenes.PLAYERS);
        }

        this.scene.launch(Scenes.BASE_CONTROLS);
        this.scene.launch(Scenes.COUNT_DOWN);

        /**
         * Unblur the game
         */
        Game.freerider.blur(0);

        /**
         * Reset Game
         */
        Game.freerider.reset();

        /**
         * Reset Scores
        */
        Game.runScore.resetScores();

        /**
         * Push down acceleration key
         */
        Game.freerider.setKeyDown(KeyCodes.ACCELERATE[0]);

        /**
         * Bind to scene and game events
         */
        this.bindEvents();

        /**
         * Enable shutdown method
         */
        this.enableShutdown();

        /**
         * Use the checkpoint if available
         */
        if (options.useCheckpoint)
        {
            this.checkpointUsed = true;

            this.useCheckpoint();

            Game.freerider.update()
        }
        else
        {
            this.checkpointUsed = false;

            Network.logEvent(AnalyticsEvents.PLAYER_PLAY);
        }

        if( Game.adsManager.hasRewardedVideoSupport() )
        {
            this.loadRewardedVideo();
        }
    }

    /**
     * Bind to events
     */
    private bindEvents()
    {
        EventEmitter.once(Scenes.COUNT_DOWN, 'complete', this.startGame);
        EventEmitter.once(Scenes.COMPLETE, 'scoreShown', this.hideScore);
        EventEmitter.once(Scenes.COMPLETE, 'playAgain', this.showInterstitial);
        EventEmitter.on(Scenes.CONTINUE, 'videoWatched', this.restartAtCheckpoint);
        EventEmitter.on(Scenes.INTERSTITIAL, 'complete', this.restart);

        EventEmitter.on(EventChannels.PLAYER, 'checkpoint', this.addCheckpoint);
        EventEmitter.on(EventChannels.PLAYER, 'dead', this.launchContinueScreen);
        EventEmitter.on(EventChannels.PLAYER, 'star_hit', this.playerComplete)

    }

    /**
     * Unbind from events
     */
    private unbindEvents()
    {
        EventEmitter.off(Scenes.COUNT_DOWN, 'complete', this.startGame);
        EventEmitter.off(Scenes.COMPLETE, 'scoreShown', this.hideScore);
        EventEmitter.off(Scenes.COMPLETE, 'playAgain', this.showInterstitial);
        EventEmitter.off(Scenes.CONTINUE, 'videoWatched', this.restartAtCheckpoint);
        EventEmitter.off(Scenes.INTERSTITIAL, 'complete', this.restart);

        EventEmitter.off(EventChannels.PLAYER, 'checkpoint', this.addCheckpoint);
        EventEmitter.off(EventChannels.PLAYER, 'dead', this.launchContinueScreen);
        EventEmitter.off(EventChannels.PLAYER, 'star_hit', this.playerComplete)
    }

    /**
     * Fires when player hits end star
     */
    private playerComplete = () =>
    {
        Network.logEvent(AnalyticsEvents.PLAYER_COMPLETE);
    }

    /**
     * Show interstitials
     */
    private showInterstitial = () =>
    {
        this.scene.launch(Scenes.INTERSTITIAL);
    }

    /**
     * Restart at checkpoint
     */
    private restartAtCheckpoint = async () =>
    {
        this.playing = false;

        //Wait for a tick
        await Delay(100);

        this.scene.restart({ useCheckpoint: true });
    }

    /**
     * Loads rewarded video if not already loaded
     */
    private loadRewardedVideo()
    {
        if (Game.adsManager.isRewardedVideoLoaded() === false)
        {
            Game.adsManager.loadRewardedVideo().catch(error =>
            {
                Network.logEvent(AnalyticsEvents.FAILED_TO_LOAD_VIDEO, null, { error_code: error.code, non_interaction:'true' });
            });
        }
    }

    /**
     * Use checkpoint
     */
    private useCheckpoint()
    {
        /**
         * Remove checkpoint from the queue
         */
        const checkpoint = this.checkpoints.pop();

        /**
         * Player
         */
        const player = Game.freerider.getPlayer();

        /**
         * Import checkpoint player into current player
         */
        player.import(checkpoint.player);

        /**
         * Merge checkpoint score into current players score
         */
        Extend(Game.runScore, checkpoint.score);
    }

    /**
     * Adds player checkpoint
     * @param playerExported
     */
    private addCheckpoint = (playerExported: FreeRider.PlayerExported) =>
    {
        const playerScore = ObjectExport(Game.runScore);

        this.checkpoints.push({
            score: playerScore,
            player: playerExported
        })
    }

    /**
     * Launch Complete Screen
     */
    public launchContinueScreen = async () =>
    {
        /*---------- Log Death Event ---------*/
        const currentDistance = Game.runScore.getDistanceScore();

        Network.logEvent(AnalyticsEvents.PLAYER_DEATH, currentDistance);

        /*------------------------------------*/

        this.scene.stop(Scenes.BASE_CONTROLS);

        await Delay(500);

        Game.freerider.blur(6);

        if (this.checkpoints.length > 0 && this.checkpointUsed === false)
        {
            this.scene.launch(Scenes.CONTINUE);
        }
        else
        {
            this.scene.launch(Scenes.COMPLETE);
        }
    }

    /**
     * Restart Scene
     */
    private restart = () =>
    {
        this.checkpoints = [];

        this.scene.restart({ useCheckpoint: false });
    }

    /**
     * Hides score
     */
    public hideScore = () =>
    {
        this.scene.stop(Scenes.SCORE);
    }

    /**
     * Start the game
     */
    private startGame = () =>
    {
        this.scene.launch(Scenes.SCORE);

        this.playing = true;
    }

    /**
     * Updates the current scene
     * @param time
     * @param delta
     */
    public update()
    {
        if (this.playing)
        {
            Game.freerider.update();
        }

        Game.freerider.render();
    }

    /**
     * Shuts down scenes
     */
    public shutdown()
    {
        this.unbindEvents();

        this.scene.stop(Scenes.SCORE);
        this.scene.stop(Scenes.BASE_CONTROLS);
        this.scene.stop(Scenes.COUNT_DOWN);
        this.scene.stop(Scenes.CONTINUE);
        this.scene.stop(Scenes.COMPLETE);
        this.scene.stop(Scenes.PLAYERS);
        this.scene.stop(Scenes.INTERSTITIAL);
        this.scene.stop(Scenes.CONTROLS_TUTORIAL);

        Game.freerider.clearCanvas();
    }


}

export default MainScene;